/* Bloco 9 — Conta Corrente de Colaboradores e Operadores. */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import {
  Wallet, ArrowDown, ArrowUp, Plus, Calendar, Users, BarChart3,
  RefreshCw, X, ChevronRight, History, CheckCircle2,
  Info, Pencil, Trash2, ClipboardCheck, Repeat, FileText, Mail, AlertTriangle,
} from 'lucide-react';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatBRL = (n) => Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const todayISO = () => new Date().toISOString().slice(0, 10);
const monthRange = () => {
  // Default: últimos 12 meses (mais inclusivo para mostrar movimentação histórica)
  const d = new Date();
  const df = d.toISOString().slice(0, 10);
  const di = new Date(d.getFullYear() - 1, d.getMonth(), 1).toISOString().slice(0, 10);
  return { data_inicio: di, data_fim: df };
};

const ORIGEM_LABELS = {
  horas_os: 'Horas em OS', adiantamento: 'Adiantamento', adiantamento_viagem: 'Adiantamento de Viagem',
  diaria: 'Diária', acerto: 'Acerto', acerto_horas: 'Acerto de Horas', acerto_colaborador: 'Acerto de Colaborador',
  ajuste_manual: 'Ajuste Manual', manual: 'Ajuste Manual',
  salario: 'Salário', ferias: 'Férias', '13o_salario': '13º Salário',
  salario_mensal: 'Salário Mensal',
  brinde_premio_doacao: 'Brindes/Prêmios/Doações', doacao: 'Doação',
  prestacao_contas: 'Prestação de Contas',
  financeiro_pessoal: 'Financeiro (Pessoal)', financeiro_sync: 'Sync Financeiro',
};

const TIPO_PESSOA_LABELS = { colaborador: 'Colaborador', operador: 'Operador' };

// Rastreabilidade (Jul/2026): link clicável para a O.S. de origem do lançamento
function OsLink({ l }) {
  if (!l?.os_numero) return null;
  return (
    <a
      href={`/work-orders?search=${encodeURIComponent('#' + l.os_numero)}`}
      data-testid={`cc-os-link-${l.id}`}
      className="text-yellow-400 hover:text-yellow-300 underline underline-offset-2 font-mono"
      title={`Abrir O.S. #${l.os_numero}${l.os_info?.maquina ? ` — ${l.os_info.maquina}` : ''}${l.os_info?.operation_type ? ` (${l.os_info.operation_type})` : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      O.S. #{l.os_numero}
    </a>
  );
}

// Data ISO → DD/MM/YYYY
function fmtDataBR(iso) {
  return iso ? iso.slice(0, 10).split('-').reverse().join('/') : '';
}

// Descrição completa do lançamento horas_os — formato Jul/2026:
// "10/07/2026, ORIGEM EM OS #461 — 14.00h × R$ 50.00/h"
function descricaoHorasOs(l) {
  const osTag = l.os_numero ? ` #${l.os_numero}` : '';
  const dataTag = l.data ? `${fmtDataBR(l.data)}, ` : '';
  const horas = Number(l.horas || 0);
  const vh = Number(l.valor_hora_aplicado || 0);
  if (l.valor_hora_pendente || vh <= 0) {
    return `${dataTag}ORIGEM EM OS${osTag} — ${horas.toFixed(2)}h (valor/hora pendente)`;
  }
  return `${dataTag}ORIGEM EM OS${osTag} — ${horas.toFixed(2)}h × R$ ${vh.toFixed(2)}/h`;
}

// Dedupe de requisições em voo (escopo de módulo): remontagens do componente
// (StrictMode/hidratação) compartilham a MESMA requisição em vez de duplicá-la
const resumoInflight = {};

export default function ContaCorrente() {
  const [view, setView] = useState('gerencial'); // 'gerencial' | 'detalhe'
  const [periodo, setPeriodo] = useState(monthRange);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [situacao, setSituacao] = useState('ativos'); // ativos | inativos | todos
  const [filtroOrigem, setFiltroOrigem] = useState(''); // grupo de origem no detalhe
  const [filtroStatusLanc, setFiltroStatusLanc] = useState('ativo'); // ativo | estornado | todos
  const [busca, setBusca] = useState('');
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);

  // Visão detalhe
  const [selecionada, setSelecionada] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [filtroCC, setFiltroCC] = useState('');

  // Modais
  const [showDiariaModal, setShowDiariaModal] = useState(false);
  const [showAcertoModal, setShowAcertoModal] = useState(false);
  const [editLanc, setEditLanc] = useState(null);
  const [showConciliacao, setShowConciliacao] = useState(false);
  const [showSyncFin, setShowSyncFin] = useState(false);
  const [showIndevidos, setShowIndevidos] = useState(false);
  const [showRevisaoHorasOs, setShowRevisaoHorasOs] = useState(false);

  // Sequenciador de requisições: garante que só a resposta MAIS RECENTE seja aplicada
  // e que o loading resolva corretamente (evita tempestade de fetch concorrente)
  const fetchSeqRef = useRef(0);

  const fetchResumo = async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        data_inicio: periodo.data_inicio, data_fim: periodo.data_fim,
        situacao,
        ...(filtroTipo ? { pessoa_tipo: filtroTipo } : {}),
        ...(busca ? { busca } : {}),
      }).toString();
      if (!resumoInflight[qs]) {
        resumoInflight[qs] = fetch(`${API_URL}/api/conta-corrente/resumo-gerencial?${qs}`, { headers: authHeaders() })
          .then((r) => (r.ok ? r.json() : null))
          .finally(() => { setTimeout(() => { delete resumoInflight[qs]; }, 0); });
      }
      const data = await resumoInflight[qs];
      if (seq !== fetchSeqRef.current) return; // resposta obsoleta: descarta
      if (data) setResumo(data);
    } catch (e) { /* */ }
    if (seq === fetchSeqRef.current) setLoading(false);
  };

  const fetchCentrosCusto = async () => {
    try {
      const r = await fetch(`${API_URL}/api/centros-custo`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setCentrosCusto(Array.isArray(d) ? d : (d.items || []));
      }
    } catch (e) { /* */ }
  };

  // Mapeamento dos grupos de filtro de origem (Melhoria Ago/2026)
  const ORIGEM_GRUPOS = {
    salario: 'salario_mensal,salario,13o_salario,ferias',
    horas_os: 'horas_os,diaria_os',
    diaria: 'diaria',
    adiantamento: 'adiantamento,adiantamento_viagem',
    acerto: 'acerto,acerto_colaborador,ajuste_manual',
    financeiro: 'financeiro_sync,financeiro_pessoal,prestacao_contas,brinde_premio_doacao',
  };

  const detalheSeqRef = useRef(0);
  const fetchDetalhe = async (cc) => {
    const seq = ++detalheSeqRef.current;
    try {
      const params = { ...periodo };
      if (filtroCC) params.centro_de_custo_id = filtroCC;
      if (filtroOrigem && ORIGEM_GRUPOS[filtroOrigem]) params.origens = ORIGEM_GRUPOS[filtroOrigem];
      if (filtroStatusLanc && filtroStatusLanc !== 'ativo') params.status_lancamento = filtroStatusLanc;
      const qs = new URLSearchParams(params).toString();
      const r = await fetch(
        `${API_URL}/api/conta-corrente/${cc.pessoa_tipo}/${cc.pessoa_id}?${qs}`,
        { headers: authHeaders() },
      );
      if (seq !== detalheSeqRef.current) return; // resposta obsoleta: descarta
      if (r.ok) setDetalhe(await r.json());
    } catch (e) { /* */ }
  };

  useEffect(() => { fetchCentrosCusto(); /* eslint-disable-next-line */ }, []);
  // Efeito ÚNICO para o resumo: dispara em período/tipo/busca, com debounce apenas
  // quando há busca digitada. O cleanup cancela timeouts pendentes (evita fetch duplicado)
  useEffect(() => {
    const t = setTimeout(() => { fetchResumo(); }, busca ? 350 : 0);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [periodo, filtroTipo, busca, situacao]);
  useEffect(() => {
    if (selecionada) fetchDetalhe(selecionada);
    /* eslint-disable-next-line */
  }, [filtroCC, periodo, filtroOrigem, filtroStatusLanc]);

  const pessoasFiltradas = useMemo(() => {
    if (!resumo?.pessoas) return [];
    const b = busca.toLowerCase();
    return resumo.pessoas.filter(p => !b || p.pessoa_nome.toLowerCase().includes(b));
  }, [resumo, busca]);

  const runMigrate = async () => {
    const r = await fetch(`${API_URL}/api/conta-corrente/migrate`, {
      method: 'POST', headers: authHeaders(),
    });
    if (r.ok) {
      const d = await r.json();
      toast.success(`Migração concluída: ${d.colaboradores_criados} colaborador(es) + ${d.operadores_criados} operador(es)`);
      fetchResumo();
    }
  };

  const runGerarSalarios = async () => {
    const r = await fetch(`${API_URL}/api/conta-corrente/gerar-salarios-mensais`, {
      method: 'POST', headers: authHeaders(),
    });
    if (r.ok) {
      const d = await r.json();
      toast.success(`Salários gerados: ${d.creditos_criados} novo(s) crédito(s) — ${d.colaboradores_processados} colaborador(es)`);
      fetchResumo();
      if (selecionada) fetchDetalhe(selecionada);
    } else {
      toast.error('Falha ao gerar salários mensais');
    }
  };

  // Modais — renderizados em qualquer view (precisam estar disponíveis quando view='detalhe' também)
  const modais = (
    <>
      {showDiariaModal && selecionada && (
        <DiariaModal pessoa={selecionada} onClose={() => setShowDiariaModal(false)}
          onSaved={() => { setShowDiariaModal(false); fetchDetalhe(selecionada); fetchResumo(); }} />
      )}
      {showAcertoModal && selecionada && detalhe && (
        <AcertoModal pessoa={selecionada} cc={detalhe.conta_corrente}
          onClose={() => setShowAcertoModal(false)}
          onSaved={() => { setShowAcertoModal(false); fetchDetalhe(selecionada); fetchResumo(); }} />
      )}
      {editLanc && (
        <EditLancamentoModal lanc={editLanc} onClose={() => setEditLanc(null)}
          onSaved={() => { setEditLanc(null); fetchDetalhe(selecionada); fetchResumo(); }} />
      )}
      {showConciliacao && (
        <ConciliacaoModal onClose={() => setShowConciliacao(false)}
          onApplied={() => { fetchResumo(); if (selecionada) fetchDetalhe(selecionada); }} />
      )}
      {showSyncFin && (
        <SyncFinanceiroModal onClose={() => setShowSyncFin(false)}
          onApplied={() => { fetchResumo(); if (selecionada) fetchDetalhe(selecionada); }} />
      )}
      {showIndevidos && (
        <CreditosIndevidosModal onClose={() => setShowIndevidos(false)}
          onApplied={() => { fetchResumo(); if (selecionada) fetchDetalhe(selecionada); }} />
      )}
      {showRevisaoHorasOs && (
        <RevisaoHorasOsModal onClose={() => setShowRevisaoHorasOs(false)}
          onApplied={() => { fetchResumo(); if (selecionada) fetchDetalhe(selecionada); }} />
      )}
    </>
  );

  // ======================== VIEW: detalhe ========================
  if (view === 'detalhe' && selecionada) {
    return (
      <>
        <ContaCorrenteDetalhe
          pessoa={selecionada}
          periodo={periodo}
          setPeriodo={setPeriodo}
          detalhe={detalhe}
          centrosCusto={centrosCusto}
          filtroCC={filtroCC}
          setFiltroCC={setFiltroCC}
          filtroOrigem={filtroOrigem}
          setFiltroOrigem={setFiltroOrigem}
          filtroStatusLanc={filtroStatusLanc}
          setFiltroStatusLanc={setFiltroStatusLanc}
          onBack={() => { setView('gerencial'); setSelecionada(null); setDetalhe(null); setFiltroCC(''); setFiltroOrigem(''); setFiltroStatusLanc('ativo'); }}
          onRefresh={() => { fetchDetalhe(selecionada); fetchResumo(); }}
          onOpenDiaria={() => setShowDiariaModal(true)}
          onOpenAcerto={() => setShowAcertoModal(true)}
          onEditLanc={(l) => setEditLanc(l)}
          onDeleteLanc={async (l) => {
            if (!window.confirm(`Excluir o lançamento de ${formatBRL(l.valor)} de ${l.data}?`)) return;
            const r = await fetch(`${API_URL}/api/conta-corrente/lancamento/${l.id}`, {
              method: 'DELETE', headers: authHeaders(),
            });
            if (r.ok) { toast.success('Lançamento excluído'); fetchDetalhe(selecionada); fetchResumo(); }
            else { const d = await r.json(); toast.error(d.detail || 'Erro ao excluir'); }
          }}
        />
        {modais}
      </>
    );
  }

  // ======================== VIEW: gerencial ========================
  return (
    <div className="space-y-6" data-testid="conta-corrente-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wallet className="text-emerald-400" /> Conta Corrente
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Adiantamentos, diárias e créditos por horas por colaborador / operador
          </p>
        </div>
        <button onClick={runMigrate}
          className="flex items-center gap-1 text-xs px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg"
          data-testid="cc-migrate-btn" title="Cria CC para colaboradores/operadores sem registro">
          <RefreshCw size={14} /> Sincronizar pessoas
        </button>
        <button onClick={runGerarSalarios}
          className="flex items-center gap-1 text-xs px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-lg ml-2"
          data-testid="cc-gerar-salarios-btn"
          title="Gera o crédito de salário mensal (colaboradores ativos com salário base). Idempotente.">
          <Wallet size={14} /> Gerar Salários
        </button>
        <button onClick={() => setShowConciliacao(true)}
          className="flex items-center gap-1 text-xs px-3 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-lg ml-2"
          data-testid="cc-open-conciliacao-btn"
          title="Concilia O.S. fechadas sem crédito de horas">
          <ClipboardCheck size={14} /> Conciliação O.S.
        </button>
        <button onClick={() => setShowIndevidos(true)}
          className="flex items-center gap-1 text-xs px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30 rounded-lg ml-2"
          data-testid="cc-open-indevidos-btn"
          title="Revisar e estornar créditos de salário automático gerados indevidamente (Horista Puro/sem tipo)">
          <AlertTriangle size={14} /> Créditos Indevidos
        </button>
        <button onClick={() => setShowRevisaoHorasOs(true)}
          className="flex items-center gap-1 text-xs px-3 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-lg ml-2"
          data-testid="cc-open-revisao-horas-os-btn"
          title="Revisar créditos de Horas em O.S. com valor divergente de (horas da O.S. × valor/hora do operador)">
          <ClipboardCheck size={14} /> Revisão Horas O.S.
        </button>
        <button onClick={() => setShowSyncFin(true)}
          className="flex items-center gap-1 text-xs px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded-lg ml-2"
          data-testid="cc-open-sync-fin-btn"
          title="Sincroniza lançamentos do Financeiro com responsável colaborador/operador">
          <Repeat size={14} /> Sync Financeiro
        </button>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-[#1A1A1A] border border-gray-800 rounded-xl p-4">
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Início</label>
          <input type="date" value={periodo.data_inicio}
            onChange={(e) => setPeriodo({ ...periodo, data_inicio: e.target.value })}
            data-testid="cc-data-inicio"
            className="w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Fim</label>
          <input type="date" value={periodo.data_fim}
            onChange={(e) => setPeriodo({ ...periodo, data_fim: e.target.value })}
            data-testid="cc-data-fim"
            className="w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Tipo</label>
          <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}
            data-testid="cc-filtro-tipo"
            className="w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm">
            <option value="">Todos</option>
            <option value="colaborador">Colaboradores</option>
            <option value="operador">Operadores</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Buscar</label>
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Nome..."
            data-testid="cc-busca"
            className="w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
        </div>
      </div>

      {/* Melhoria Ago/2026 — Segregação Ativos / Inativos */}
      <div className="flex gap-1 bg-[#0A0A0A] border border-gray-800 rounded-lg p-1 w-fit" data-testid="cc-situacao-tabs">
        {[['ativos', 'Ativos'], ['inativos', 'Inativos'], ['todos', 'Todos']].map(([val, label]) => (
          <button key={val} onClick={() => setSituacao(val)}
            data-testid={`cc-tab-${val}`}
            className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${
              situacao === val ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'text-gray-400 hover:text-white'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Cards totais — Jul/2026: inclui Entradas/Saídas restritas ao período selecionado */}
      {resumo && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="cc-resumo-cards">
          <Card label="Adiantamentos" value={resumo.total_adiantamentos} color="red" icon={<ArrowDown size={18} />} />
          <Card label="Diárias" value={resumo.total_diarias} color="yellow" icon={<Calendar size={18} />} />
          <Card label="Créditos (horas)" value={resumo.total_creditos_horas} color="emerald" icon={<ArrowUp size={18} />} />
          <Card label="Entradas no Período" value={resumo.total_entradas_periodo} color="emerald" icon={<ArrowUp size={18} />} />
          <Card label="Saídas no Período" value={resumo.total_saidas_periodo} color="red" icon={<ArrowDown size={18} />} />
          <Card label="Saldo Consolidado" value={resumo.saldo_consolidado}
                color={resumo.saldo_consolidado >= 0 ? 'blue' : 'rose'} icon={<BarChart3 size={18} />} />
        </div>
      )}

      {/* Tabela de pessoas */}
      <div className="bg-[#1A1A1A] border border-gray-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <Users size={16} className="text-gray-400" /> Pessoas ({pessoasFiltradas.length})
          </h2>
        </div>
        {loading ? (
          <div className="p-10 text-center text-gray-400">Carregando...</div>
        ) : pessoasFiltradas.length === 0 ? (
          <div className="p-10 text-center text-gray-400">Nenhuma pessoa encontrada.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cc-tabela-pessoas">
              <thead className="bg-[#0A0A0A] text-gray-400 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Nome</th>
                  <th className="text-left px-4 py-2 hidden sm:table-cell">Tipo</th>
                  <th className="text-right px-4 py-2">Créditos</th>
                  <th className="text-right px-4 py-2">Débitos</th>
                  <th className="text-right px-4 py-2">Saldo Atual</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {pessoasFiltradas.map(p => (
                  <tr key={`${p.pessoa_tipo}_${p.pessoa_id}`}
                      onClick={() => { setSelecionada(p); setView('detalhe'); fetchDetalhe(p); }}
                      data-testid={`cc-row-${p.pessoa_id}`}
                      className="border-t border-gray-800 hover:bg-gray-800/30 cursor-pointer">
                    <td className="px-4 py-2 text-white">{p.pessoa_nome}</td>
                    <td className="px-4 py-2 text-gray-400 hidden sm:table-cell">
                      {TIPO_PESSOA_LABELS[p.pessoa_tipo] || p.pessoa_tipo}
                    </td>
                    <td className="px-4 py-2 text-right text-emerald-400">{formatBRL(p.creditos_periodo)}</td>
                    <td className="px-4 py-2 text-right text-red-400">{formatBRL(p.debitos_periodo)}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${p.saldo_atual >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatBRL(p.saldo_atual)}
                    </td>
                    <td className="px-2 py-2 text-gray-500"><ChevronRight size={14} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modais (acessíveis em qualquer view) */}
      {modais}
    </div>
  );
}

// =============================================================================
// Componentes auxiliares
// =============================================================================

function Card({ label, value, color = 'emerald', icon }) {
  const colorMap = {
    emerald: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/5',
    red: 'text-red-400 border-red-500/40 bg-red-500/5',
    yellow: 'text-yellow-400 border-yellow-500/40 bg-yellow-500/5',
    blue: 'text-blue-400 border-blue-500/40 bg-blue-500/5',
    rose: 'text-rose-400 border-rose-500/40 bg-rose-500/5',
  };
  return (
    <div className={`border rounded-xl p-3 ${colorMap[color]}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide opacity-80">
        {icon} {label}
      </div>
      <div className="text-lg sm:text-xl font-bold mt-1">{formatBRL(value)}</div>
    </div>
  );
}

function ContaCorrenteDetalhe({ pessoa, periodo, setPeriodo, detalhe, centrosCusto, filtroCC, setFiltroCC, filtroOrigem, setFiltroOrigem, filtroStatusLanc, setFiltroStatusLanc, onBack, onRefresh, onOpenDiaria, onOpenAcerto, onEditLanc, onDeleteLanc }) {
  const [reciboComp, setReciboComp] = useState(() => new Date().toISOString().slice(0, 7));
  const [baixando, setBaixando] = useState(false);
  const [envios, setEnvios] = useState([]);
  const [salarioComp, setSalarioComp] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [gerandoSalario, setGerandoSalario] = useState(false);

  const pessoaTipo = detalhe?.conta_corrente?.pessoa_tipo;
  const pessoaId = detalhe?.conta_corrente?.pessoa_id;

  const fetchEnvios = async () => {
    if (!pessoaTipo || !pessoaId) return;
    try {
      const r = await fetch(`${API_URL}/api/conta-corrente/${pessoaTipo}/${pessoaId}/recibo-envios`,
        { headers: authHeaders() });
      if (r.ok) setEnvios((await r.json()).envios || []);
    } catch (_e) { /* silencioso */ }
  };

  useEffect(() => { fetchEnvios(); /* eslint-disable-next-line */ }, [pessoaTipo, pessoaId]);

  // Melhoria Ago/2026 — geração de salário individual (idempotente)
  const gerarSalarioIndividual = async () => {
    if (!salarioComp) { toast.error('Selecione a competência'); return; }
    setGerandoSalario(true);
    try {
      const r = await fetch(
        `${API_URL}/api/conta-corrente/colaborador/${pessoaId}/gerar-salario?competencia=${salarioComp}`,
        { method: 'POST', headers: authHeaders() },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Erro ao gerar salário'); return; }
      if (d.status === 'gerado') {
        toast.success(`Salário de ${salarioComp} gerado: ${formatBRL(d.valor)}`);
        onRefresh();
      } else if (d.status === 'ja_existia') {
        toast.info(`Salário de ${salarioComp} já existia (${formatBRL(d.valor)}) — nada foi duplicado`);
      } else {
        toast.warning(d.motivo || 'Colaborador não elegível para salário automático');
      }
    } catch (_e) { toast.error('Erro ao gerar salário'); }
    finally { setGerandoSalario(false); }
  };

  const ultimoEnvioSucesso = envios.find(e => e.competencia === reciboComp && e.status === 'sent');

  const baixarPDF = async (url, fallbackName) => {
    setBaixando(true);
    try {
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) {
        let msg = 'Falha ao gerar o recibo';
        try { msg = (await r.json()).detail || msg; } catch (_e) {}
        toast.error(msg);
        return;
      }
      const blob = await r.blob();
      const dispo = r.headers.get('content-disposition') || '';
      const m = dispo.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : fallbackName;
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl; a.download = name; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(objUrl);
      toast.success('Recibo gerado');
    } catch (_e) {
      toast.error('Erro ao baixar o recibo');
    } finally {
      setBaixando(false);
    }
  };

  if (!detalhe) return <div className="text-gray-400 p-8">Carregando...</div>;
  const cc = detalhe.conta_corrente;
  const apr = detalhe.apuracao;
  const baixarReciboIndividual = () => baixarPDF(
    `${API_URL}/api/conta-corrente/${cc.pessoa_tipo}/${cc.pessoa_id}/recibo?competencia=${reciboComp}`,
    `recibo_${reciboComp}.pdf`,
  );
  // Jul/2026 — recibo por período arbitrário (horistas: ciclo de faturamento próprio)
  const baixarReciboPeriodo = () => baixarPDF(
    `${API_URL}/api/conta-corrente/${cc.pessoa_tipo}/${cc.pessoa_id}/recibo?data_inicio=${periodo.data_inicio}&data_fim=${periodo.data_fim}`,
    `recibo_periodo_${periodo.data_inicio}_a_${periodo.data_fim}.pdf`,
  );
  const baixarReciboLote = () => baixarPDF(
    `${API_URL}/api/conta-corrente/recibos-lote?competencia=${reciboComp}`,
    `recibos_${reciboComp}.pdf`,
  );
  const enviarRecibo = async () => {
    if (ultimoEnvioSucesso) {
      const dt = new Date(ultimoEnvioSucesso.enviado_em).toLocaleString('pt-BR');
      if (!window.confirm(`Este recibo já foi enviado em ${dt} para ${ultimoEnvioSucesso.email}. Deseja reenviar?`)) return;
    }
    setBaixando(true);
    try {
      const r = await fetch(`${API_URL}/api/conta-corrente/${cc.pessoa_tipo}/${cc.pessoa_id}/recibo/enviar?competencia=${reciboComp}`,
        { method: 'POST', headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (r.ok) toast.success(`Recibo enviado para ${d.email || 'o colaborador'}`);
      else toast.error(d.detail || 'Falha ao enviar o recibo');
    } catch (_e) { toast.error('Erro ao enviar o recibo'); }
    finally { setBaixando(false); fetchEnvios(); }
  };
  const enviarReciboLote = async () => {
    setBaixando(true);
    try {
      const r = await fetch(`${API_URL}/api/conta-corrente/recibos-lote/enviar?competencia=${reciboComp}`,
        { method: 'POST', headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        const semEmail = (d.sem_email || []).length;
        toast.success(`${d.enviados} recibo(s) enviado(s)${semEmail ? ` · ${semEmail} sem e-mail` : ''}`);
      } else toast.error(d.detail || 'Falha no envio em lote');
    } catch (_e) { toast.error('Erro no envio em lote'); }
    finally { setBaixando(false); fetchEnvios(); }
  };
  return (
    <div className="space-y-5" data-testid="cc-detalhe-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={onBack} className="text-sm text-gray-400 hover:text-white flex items-center gap-1">
          <ChevronRight size={14} className="rotate-180" /> Voltar
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filtroCC}
            onChange={(e) => setFiltroCC(e.target.value)}
            data-testid="cc-filtro-centro-custo"
            title="Filtrar lançamentos por Centro de Custo"
            className="bg-[#0A0A0A] border border-gray-800 rounded px-2 py-1 text-white text-xs max-w-[200px]"
          >
            <option value="">Todos os Centros de Custo</option>
            {(centrosCusto || []).map(c => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
          <select
            value={filtroOrigem}
            onChange={(e) => setFiltroOrigem(e.target.value)}
            data-testid="cc-filtro-origem"
            title="Filtrar por tipo de lançamento (atualiza os totais do período)"
            className="bg-[#0A0A0A] border border-gray-800 rounded px-2 py-1 text-white text-xs"
          >
            <option value="">Todos os Tipos</option>
            <option value="salario">Crédito de Salário</option>
            <option value="horas_os">Crédito de Horas O.S.</option>
            <option value="diaria">Diária</option>
            <option value="adiantamento">Adiantamento</option>
            <option value="acerto">Acerto / Ajuste</option>
            <option value="financeiro">Outros (Financeiro)</option>
          </select>
          <select
            value={filtroStatusLanc}
            onChange={(e) => setFiltroStatusLanc(e.target.value)}
            data-testid="cc-filtro-status-lanc"
            title="Status do lançamento"
            className="bg-[#0A0A0A] border border-gray-800 rounded px-2 py-1 text-white text-xs"
          >
            <option value="ativo">Ativos</option>
            <option value="estornado">Estornados</option>
            <option value="todos">Ativos + Estornados</option>
          </select>
          <button onClick={onRefresh} className="p-1.5 bg-gray-800 hover:bg-gray-700 rounded">
            <RefreshCw size={14} className="text-gray-300" />
          </button>
        </div>
      </div>

      <div className="bg-[#1A1A1A] border border-gray-800 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-white">{cc.pessoa_nome}</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {TIPO_PESSOA_LABELS[cc.pessoa_tipo]} {cc.pessoa_cargo ? `• ${cc.pessoa_cargo}` : ''}
              {cc.pessoa_documento ? ` • ${cc.pessoa_documento}` : ''}
            </p>
            <div className="flex gap-3 mt-2 text-[11px] text-gray-500">
              {cc.valor_hora != null && <span>R$/hora: <strong className="text-white">{formatBRL(cc.valor_hora)}</strong></span>}
              {cc.valor_diaria_padrao != null && <span>Diária padrão: <strong className="text-white">{formatBRL(cc.valor_diaria_padrao)}</strong></span>}
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase text-gray-500">Saldo Atual</p>
            <p className={`text-2xl font-bold ${cc.saldo_atual >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="cc-saldo-atual">
              {formatBRL(cc.saldo_atual)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
          <button data-testid="cc-btn-diaria" onClick={onOpenDiaria}
            className="text-xs px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 rounded-lg flex items-center gap-1 justify-center">
            <Calendar size={14} /> Diária (Crédito)
          </button>
          <button data-testid="cc-btn-acerto" onClick={onOpenAcerto}
            className="text-xs px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 rounded-lg flex items-center gap-1 justify-center">
            <CheckCircle2 size={14} /> Acerto
          </button>
          <button data-testid="cc-btn-historico" disabled
            className="text-xs px-3 py-2 bg-gray-800 text-gray-500 rounded-lg flex items-center gap-1 justify-center cursor-not-allowed">
            <History size={14} /> Histórico
          </button>
        </div>

        {/* Melhoria Ago/2026 — Geração de Salário Individual (idempotente) */}
        {cc.pessoa_tipo === 'colaborador' && (
          <div className="mt-4 pt-4 border-t border-gray-800 flex items-end gap-2 flex-wrap" data-testid="cc-salario-individual-bar">
            <div>
              <label className="text-[10px] uppercase text-gray-500 block mb-1">Salário Individual — Competência</label>
              <input type="month" value={salarioComp} onChange={(e) => setSalarioComp(e.target.value)}
                data-testid="cc-salario-competencia"
                className="bg-[#0A0A0A] border border-gray-800 rounded px-2 py-1.5 text-white text-xs" />
            </div>
            <button onClick={gerarSalarioIndividual} disabled={gerandoSalario}
              data-testid="cc-gerar-salario-individual-btn"
              title="Gera o salário desta competência apenas para este colaborador. Idempotente: não duplica."
              className="text-xs px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 rounded-lg flex items-center gap-1 disabled:opacity-50">
              <Wallet size={14} /> {gerandoSalario ? 'Gerando...' : 'Gerar Salário'}
            </button>
          </div>
        )}

        {/* Recibo / Holerite Mensal (PDF) — Bloco 9 Jun/2026 */}
        <div className="mt-4 pt-4 border-t border-gray-800 flex items-end gap-2 flex-wrap" data-testid="cc-recibo-bar">
          <div>
            <label className="text-[11px] text-gray-400 block mb-1">Competência do recibo</label>
            <input type="month" value={reciboComp}
              onChange={(e) => setReciboComp(e.target.value)}
              data-testid="cc-recibo-competencia"
              className="bg-[#0A0A0A] border border-gray-800 rounded px-2 py-1.5 text-white text-xs" />
          </div>
          <button onClick={baixarReciboIndividual} disabled={baixando}
            data-testid="cc-recibo-individual-btn"
            className="text-xs px-3 py-2 bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20 rounded-lg flex items-center gap-1 justify-center disabled:opacity-50">
            <FileText size={14} /> Gerar Recibo (PDF)
          </button>
          <button onClick={baixarReciboPeriodo} disabled={baixando}
            data-testid="cc-recibo-periodo-btn"
            title={`Gera o recibo do período filtrado acima (${fmtDataBR(periodo.data_inicio)} a ${fmtDataBR(periodo.data_fim)}) — ideal para horistas com ciclo de faturamento diferente do mês civil`}
            className="text-xs px-3 py-2 bg-purple-500/10 border border-purple-500/30 text-purple-300 hover:bg-purple-500/20 rounded-lg flex items-center gap-1 justify-center disabled:opacity-50">
            <FileText size={14} /> Recibo do Período ({fmtDataBR(periodo.data_inicio)} – {fmtDataBR(periodo.data_fim)})
          </button>
          <button onClick={baixarReciboLote} disabled={baixando}
            data-testid="cc-recibo-lote-btn"
            title="Gera 1 PDF com os recibos de todos os colaboradores com movimento nesta competência"
            className="text-xs px-3 py-2 bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700 rounded-lg flex items-center gap-1 justify-center disabled:opacity-50">
            <Users size={14} /> Recibos em lote
          </button>
          <button onClick={enviarRecibo} disabled={baixando}
            data-testid="cc-recibo-enviar-btn"
            title="Envia o recibo desta competência por e-mail ao colaborador"
            className="text-xs px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 rounded-lg flex items-center gap-1 justify-center disabled:opacity-50">
            <Mail size={14} /> Enviar por e-mail
          </button>
          <button onClick={enviarReciboLote} disabled={baixando}
            data-testid="cc-recibo-enviar-lote-btn"
            title="Envia o recibo da competência por e-mail para todos os colaboradores com e-mail cadastrado"
            className="text-xs px-3 py-2 bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700 rounded-lg flex items-center gap-1 justify-center disabled:opacity-50">
            <Mail size={14} /> Enviar p/ todos
          </button>
        </div>

        {/* Registro de envios — rastreabilidade (Bloco 9 Jun/2026) */}
        <div className="mt-3" data-testid="cc-recibo-envios">
          {ultimoEnvioSucesso ? (
            <div className="text-[11px] text-emerald-300 flex items-center gap-1.5" data-testid="cc-recibo-envio-status">
              <CheckCircle2 size={12} />
              Recibo de {reciboComp} enviado em {new Date(ultimoEnvioSucesso.enviado_em).toLocaleString('pt-BR')} para {ultimoEnvioSucesso.email}
            </div>
          ) : (
            <div className="text-[11px] text-gray-500" data-testid="cc-recibo-envio-status">
              Recibo de {reciboComp}: ainda não enviado.
            </div>
          )}
          {envios.length > 0 && (
            <details className="mt-2 group">
              <summary className="text-[11px] text-gray-400 cursor-pointer hover:text-gray-200 select-none">
                Histórico de envios ({envios.length})
              </summary>
              <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-gray-800 divide-y divide-gray-800/60">
                {envios.slice(0, 20).map(e => (
                  <div key={e.id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-[11px]" data-testid={`cc-envio-row-${e.id}`}>
                    <span className="font-mono text-gray-300">{e.competencia}</span>
                    <span className="text-gray-400 truncate flex-1">{e.email || '—'}</span>
                    <span className="text-gray-500">{new Date(e.enviado_em).toLocaleString('pt-BR')}</span>
                    <span className="text-gray-500">{ {manual: 'Manual', manual_lote: 'Lote', auto_pagamento: 'Automático'}[e.origem] || e.origem}</span>
                    {e.status === 'sent' ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Enviado</span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30" title={e.detalhe || ''}>Falhou</span>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

      </div>

      {/* Banner informativo: origem dos lançamentos */}
      <div className="bg-blue-500/10 border border-blue-500/30 text-blue-200 rounded-xl p-3 text-xs flex items-start gap-2" data-testid="cc-origem-banner">
        <Info size={14} className="text-blue-300 shrink-0 mt-0.5" />
        <div>
          <strong>Diárias</strong> são lançadas diretamente aqui na Conta Corrente como <strong>crédito positivo</strong>
          (compensação devida ao colaborador), informando horas × valor da hora.{' '}
          <strong>Adiantamentos</strong> continuam sendo registrados no módulo <strong>Financeiro</strong> com a
          categoria correspondente do grupo "Pessoal" e o responsável preenchido — a CC é atualizada automaticamente como débito.
        </div>
      </div>

      {/* Set/2026 — Filtro de Período Livre (acima dos cards de saldo) */}
      <div className="bg-[#1A1A1A] border border-gray-800 rounded-xl p-4 flex items-end gap-4 flex-wrap" data-testid="cc-detalhe-periodo-bar">
        <div className="flex items-center gap-2 text-gray-300 text-sm font-semibold">
          <Calendar size={16} className="text-yellow-500" /> Período:
        </div>
        <div>
          <label className="text-[10px] uppercase text-gray-500 block mb-1">Início</label>
          <input type="date" value={periodo.data_inicio}
            onChange={(e) => setPeriodo({ ...periodo, data_inicio: e.target.value })}
            data-testid="cc-detalhe-periodo-inicio"
            className="bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-gray-500 block mb-1">Fim</label>
          <input type="date" value={periodo.data_fim}
            onChange={(e) => setPeriodo({ ...periodo, data_fim: e.target.value })}
            data-testid="cc-detalhe-periodo-fim"
            className="bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
        </div>
        <p className="text-[11px] text-gray-500 pb-2">
          Livre seleção (sem limitação a meses fechados) — Créditos, Retiradas e os cards de saldo refletem apenas o intervalo escolhido.
        </p>
      </div>

      {/* Apuração — Parte 2: dois cards de saldo lado a lado + crédito/retirada */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="cc-saldo-cards">
        <div className={`rounded-xl p-4 border ${
          apr.saldo_no_periodo >= 0
            ? 'bg-emerald-500/5 border-emerald-500/30'
            : 'bg-rose-500/5 border-rose-500/30'
        }`} data-testid="cc-card-saldo-periodo">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400 uppercase">Saldo no período</span>
            <span className="text-[10px] text-gray-500 font-mono">
              {apr.data_inicio} → {apr.data_fim}
            </span>
          </div>
          <div className={`text-2xl font-bold font-mono ${
            apr.saldo_no_periodo >= 0 ? 'text-emerald-400' : 'text-rose-400'
          }`} data-testid="cc-saldo-periodo-valor">
            {formatBRL(apr.saldo_no_periodo)}
          </div>
          <div className="text-[11px] text-gray-500 mt-1">
            Créditos {formatBRL(apr.creditos_periodo)} − Retiradas {formatBRL(apr.retiradas_periodo)}
          </div>
        </div>
        <div className={`rounded-xl p-4 border ${
          apr.saldo_acumulado >= 0
            ? 'bg-blue-500/5 border-blue-500/30'
            : 'bg-rose-500/5 border-rose-500/30'
        }`} data-testid="cc-card-saldo-acumulado">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-400 uppercase">Saldo acumulado (histórico + período)</span>
            <span className="text-[10px] text-gray-500">para acerto</span>
          </div>
          <div className={`text-2xl font-bold font-mono ${
            apr.saldo_acumulado >= 0 ? 'text-blue-400' : 'text-rose-400'
          }`} data-testid="cc-saldo-acumulado-valor">
            {formatBRL(apr.saldo_acumulado)}
          </div>
          <div className="text-[11px] text-gray-500 mt-1">
            Saldo anterior {formatBRL(apr.saldo_anterior)} + Saldo no período {formatBRL(apr.saldo_no_periodo)}
          </div>
        </div>
      </div>

      {/* Outras Entradas Financeiras — Salário Mensal (Bloco 9 Jun/2026) */}
      {(apr.outras_entradas || []).length > 0 && (
        <div className="bg-[#1A1A1A] border border-emerald-500/30 rounded-xl overflow-hidden" data-testid="cc-bloco-outras-entradas">
          <div className="px-4 py-3 border-b border-emerald-500/30 bg-emerald-500/5 flex items-center justify-between">
            <span className="text-emerald-300 font-semibold flex items-center gap-2 text-sm">
              <Wallet size={14} /> Outras Entradas Financeiras (Salário Mensal)
            </span>
            <span className="text-emerald-400 font-mono font-bold text-sm" data-testid="cc-outras-entradas-total">
              +{formatBRL(apr.total_salario_mensal || 0)}
            </span>
          </div>
          <div className="overflow-x-auto max-h-[360px]">
            <table className="w-full text-xs">
              <thead className="bg-[#0A0A0A] text-gray-400 uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Competência</th>
                  <th className="text-left px-3 py-2">Data</th>
                  <th className="text-left px-3 py-2">Descrição</th>
                  <th className="text-right px-3 py-2">Crédito</th>
                  <th className="text-right px-3 py-2">Pago</th>
                  <th className="text-center px-3 py-2">Situação</th>
                  <th className="text-right px-3 py-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {(apr.outras_entradas || []).map(l => {
                  const pago = (apr.retiradas || [])
                    .filter(r => r.mes_referencia === l.mes_referencia)
                    .reduce((s, r) => s + (r.valor || 0), 0);
                  const quitado = pago >= (l.valor || 0) - 0.01 && pago > 0;
                  return (
                    <tr key={l.id} className="border-t border-gray-800/40 hover:bg-gray-800/20" data-testid={`cc-salario-row-${l.mes_referencia}`}>
                      <td className="px-3 py-2 text-gray-200 font-mono">{l.mes_referencia || '—'}</td>
                      <td className="px-3 py-2 text-gray-400">{l.data}</td>
                      <td className="px-3 py-2 text-gray-400 text-[11px]" title={l.descricao}>{l.descricao}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-400">+{formatBRL(l.valor)}</td>
                      <td className="px-3 py-2 text-right font-mono text-rose-300">{pago > 0 ? `−${formatBRL(pago)}` : '—'}</td>
                      <td className="px-3 py-2 text-center">
                        {quitado ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Pago</span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">Em aberto</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => onEditLanc(l)} data-testid={`cc-edit-${l.id}`}
                            className="p-1 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded">
                            <Pencil size={11} />
                          </button>
                          <button onClick={() => onDeleteLanc(l)} data-testid={`cc-delete-${l.id}`}
                            className="p-1 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded">
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-500 px-4 py-2 border-t border-gray-800">
            Crédito gerado automaticamente no 1º dia de cada mês. O pagamento de "Salários" no Financeiro (com mês de referência) baixa a competência.
          </p>
        </div>
      )}

      {/* Confronto Crédito × Retirada — Parte 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="cc-confronto-blocos">
        {/* CRÉDITOS */}
        <div className="bg-[#1A1A1A] border border-emerald-500/30 rounded-xl overflow-hidden" data-testid="cc-bloco-creditos">
          <div className="px-4 py-3 border-b border-emerald-500/30 bg-emerald-500/5 flex items-center justify-between">
            <span className="text-emerald-300 font-semibold flex items-center gap-2 text-sm">
              <ArrowUp size={14} /> Créditos
            </span>
            <span className="text-emerald-400 font-mono font-bold text-sm">
              +{formatBRL(apr.creditos_periodo)}
            </span>
          </div>
          {(apr.creditos || []).length === 0 ? (
            <p className="text-gray-500 p-6 text-center text-xs">Nenhum crédito no período.</p>
          ) : (
            <div className="overflow-x-auto max-h-[400px]">
              <table className="w-full text-xs">
                <thead className="bg-[#0A0A0A] text-gray-400 uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Data</th>
                    <th className="text-left px-3 py-2">Origem</th>
                    <th className="text-left px-3 py-2">Centro de Custo</th>
                    <th className="text-right px-3 py-2">Valor</th>
                    <th className="text-right px-3 py-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {(apr.creditos || []).map(l => (
                    <tr key={l.id} className="border-t border-gray-800/40 hover:bg-gray-800/20">
                      <td className="px-3 py-2 text-gray-300">{l.data}</td>
                      <td className="px-3 py-2 text-gray-300" title={l.origem === 'horas_os' ? descricaoHorasOs(l) : l.descricao}>
                        {ORIGEM_LABELS[l.origem] || l.origem}
                        {(l.origem === 'horas_os' || l.origem === 'diaria_os') && l.os_numero && (
                          <> — <OsLink l={l} /></>
                        )}
                        {l.valor_hora_pendente && (
                          <span className="ml-1 text-[10px] text-amber-400">(pend)</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-400 text-[11px]" title={l.centro_de_custo_id || ''}>
                        {l.centro_de_custo_nome || <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-400">
                        +{formatBRL(l.valor)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => onEditLanc(l)} data-testid={`cc-edit-${l.id}`}
                            className="p-1 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded">
                            <Pencil size={11} />
                          </button>
                          <button onClick={() => onDeleteLanc(l)} data-testid={`cc-delete-${l.id}`}
                            className="p-1 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded">
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* RETIRADAS */}
        <div className="bg-[#1A1A1A] border border-rose-500/30 rounded-xl overflow-hidden" data-testid="cc-bloco-retiradas">
          <div className="px-4 py-3 border-b border-rose-500/30 bg-rose-500/5 flex items-center justify-between">
            <span className="text-rose-300 font-semibold flex items-center gap-2 text-sm">
              <ArrowDown size={14} /> Retiradas
            </span>
            <span className="text-rose-400 font-mono font-bold text-sm">
              −{formatBRL(apr.retiradas_periodo)}
            </span>
          </div>
          {(apr.retiradas || []).length === 0 ? (
            <p className="text-gray-500 p-6 text-center text-xs">Nenhuma retirada no período.</p>
          ) : (
            <div className="overflow-x-auto max-h-[400px]">
              <table className="w-full text-xs">
                <thead className="bg-[#0A0A0A] text-gray-400 uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Data</th>
                    <th className="text-left px-3 py-2">Origem</th>
                    <th className="text-left px-3 py-2">Centro de Custo</th>
                    <th className="text-right px-3 py-2">Valor</th>
                    <th className="text-right px-3 py-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {(apr.retiradas || []).map(l => (
                    <tr key={l.id} className="border-t border-gray-800/40 hover:bg-gray-800/20">
                      <td className="px-3 py-2 text-gray-300">{l.data}</td>
                      <td className="px-3 py-2 text-gray-300" title={l.descricao}>
                        {ORIGEM_LABELS[l.origem] || l.origem}
                      </td>
                      <td className="px-3 py-2 text-gray-400 text-[11px]" title={l.centro_de_custo_id || ''}>
                        {l.centro_de_custo_nome || <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-rose-400">
                        −{formatBRL(l.valor)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => onEditLanc(l)} data-testid={`cc-edit-${l.id}`}
                            className="p-1 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded">
                            <Pencil size={11} />
                          </button>
                          <button onClick={() => onDeleteLanc(l)} data-testid={`cc-delete-${l.id}`}
                            className="p-1 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded">
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Histórico cronológico (visão tradicional, mantida) */}
      <details className="bg-[#1A1A1A] border border-gray-800 rounded-xl overflow-hidden" data-testid="cc-historico-cronologico">
        <summary className="px-4 py-3 border-b border-gray-800 text-white font-semibold flex items-center gap-2 cursor-pointer hover:bg-gray-800/30">
          <History size={16} className="text-gray-400" /> Histórico cronológico ({apr.lancamentos.length})
        </summary>
        {apr.lancamentos.length === 0 ? (
          <p className="text-gray-400 p-8 text-center text-sm">Nenhum lançamento no período.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="cc-lancamentos-tabela">
              <thead className="bg-[#0A0A0A] text-gray-400 uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Data</th>
                  <th className="text-left px-3 py-2">Origem</th>
                  <th className="text-left px-3 py-2">Descrição</th>
                  <th className="text-right px-3 py-2">Valor</th>
                </tr>
              </thead>
              <tbody>
                {apr.lancamentos.map(l => (
                  <tr key={l.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                    <td className="px-3 py-2 text-gray-300">{l.data}</td>
                    <td className="px-3 py-2"><span className="text-gray-300">{ORIGEM_LABELS[l.origem] || l.origem}</span></td>
                    <td className="px-3 py-2 text-gray-300">
                      {l.origem === 'horas_os' ? descricaoHorasOs(l) : l.descricao}
                      {(l.origem === 'horas_os' || l.origem === 'diaria_os') && l.os_numero && (
                        <> — <OsLink l={l} /></>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${l.tipo === 'credito' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {l.tipo === 'credito' ? '+' : '−'} {formatBRL(l.valor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </details>
    </div>
  );
}

// ---------------- Modais ----------------

function ModalShell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-gray-800 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
           data-testid={testid}>
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <h3 className="font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// Bloco 9 (Fev/2026 — revisado): Diária entrada direta na CC = CRÉDITO positivo
function DiariaModal({ pessoa, onClose, onSaved }) {
  const [form, setForm] = useState({
    data: todayISO(), horas: '', valor_hora: '', descricao: '',
  });
  const [saving, setSaving] = useState(false);
  const total = useMemo(() => {
    const h = Number(form.horas) || 0;
    const v = Number(form.valor_hora) || 0;
    return h > 0 && v > 0 ? h * v : 0;
  }, [form.horas, form.valor_hora]);

  const submit = async () => {
    const h = Number(form.horas);
    const v = Number(form.valor_hora);
    if (!h || h <= 0) { toast.error('Informe as horas (> 0)'); return; }
    if (!v || v <= 0) { toast.error('Informe o valor da hora (> 0)'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/conta-corrente/diaria`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          pessoa_tipo: pessoa.pessoa_tipo, pessoa_id: pessoa.pessoa_id,
          data: form.data, horas: h, valor_hora: v,
          descricao: form.descricao || null,
        }),
      });
      if (r.ok) { toast.success(`Diária registrada — crédito de ${formatBRL(h * v)}`); onSaved(); }
      else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
    } finally { setSaving(false); }
  };

  return (
    <ModalShell title={`Diária — ${pessoa.pessoa_nome}`} onClose={onClose} testid="cc-diaria-modal">
      <div className="space-y-3">
        <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 rounded-lg p-2 text-[11px] flex items-start gap-2">
          <Info size={12} className="text-emerald-300 shrink-0 mt-0.5" />
          <span>A diária é uma compensação devida ao colaborador.
            O lançamento entra como <strong>crédito positivo</strong> na Conta Corrente.</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-gray-400">Data
            <input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
              data-testid="cc-dia-data"
              className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
          </label>
          <label className="text-xs text-gray-400">Horas *
            <input type="number" min="0.01" step="0.5" value={form.horas}
              onChange={(e) => setForm({ ...form, horas: e.target.value })}
              data-testid="cc-dia-horas" placeholder="ex: 8"
              className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
          </label>
        </div>
        <label className="text-xs text-gray-400 block">Valor da hora / serviço (R$) *
          <input type="number" min="0.01" step="0.01" value={form.valor_hora}
            onChange={(e) => setForm({ ...form, valor_hora: e.target.value })}
            data-testid="cc-dia-valor-hora" placeholder="ex: 30.00"
            className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
        </label>
        <div className="flex justify-between items-center bg-[#0A0A0A] border border-emerald-500/30 rounded-lg px-3 py-2">
          <span className="text-xs text-gray-400">Total calculado:</span>
          <strong className="text-emerald-400 font-mono" data-testid="cc-dia-total">
            {formatBRL(total)}
          </strong>
        </div>
        <label className="text-xs text-gray-400 block">Descrição (opcional)
          <textarea rows={2} value={form.descricao}
            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            data-testid="cc-dia-descricao"
            className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm">Cancelar</button>
          <button onClick={submit} disabled={saving || total <= 0} data-testid="cc-dia-confirm"
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-lg text-sm disabled:opacity-50">
            Registrar
          </button>
        </div>
      </div>
    </ModalShell>
  );
}


function AcertoModal({ pessoa, cc, onClose, onSaved }) {
  const saldoDevedor = cc.saldo_atual < 0 ? -cc.saldo_atual : 0;
  const [form, setForm] = useState({
    data_acerto: todayISO(),
    valor_acertado: saldoDevedor > 0 ? saldoDevedor : '',
    observacoes: '',
    forma_pagamento: 'transferencia',
    gerar_lancamento_caixa: true,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.valor_acertado || Number(form.valor_acertado) <= 0) { toast.error('Valor inválido'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/conta-corrente/acerto`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          pessoa_tipo: pessoa.pessoa_tipo, pessoa_id: pessoa.pessoa_id,
          ...form, valor_acertado: Number(form.valor_acertado),
        }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Acerto registrado. Saldo remanescente: ${formatBRL(d.conta_corrente.saldo_atual)}`);
        onSaved();
      } else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
    } finally { setSaving(false); }
  };
  return (
    <ModalShell title={`Acerto — ${pessoa.pessoa_nome}`} onClose={onClose} testid="cc-acerto-modal">
      <div className="space-y-3">
        <div className="text-xs bg-[#0A0A0A] border border-gray-800 rounded p-2 flex justify-between">
          <span>Saldo Atual:</span>
          <strong className={cc.saldo_atual >= 0 ? 'text-emerald-400' : 'text-red-400'} data-testid="cc-acerto-saldo">
            {formatBRL(cc.saldo_atual)}
          </strong>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-gray-400">Data
            <input type="date" value={form.data_acerto}
              onChange={(e) => setForm({ ...form, data_acerto: e.target.value })}
              className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
          </label>
          <label className="text-xs text-gray-400">Valor a acertar *
            <input type="number" min="0" step="0.01" value={form.valor_acertado}
              onChange={(e) => setForm({ ...form, valor_acertado: e.target.value })}
              data-testid="cc-acerto-valor"
              className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
          </label>
        </div>
        <label className="text-xs text-gray-400 block">Observações
          <textarea rows={2} value={form.observacoes}
            onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
            className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
        </label>
        <label className="text-xs text-gray-400 flex items-center gap-2">
          <input type="checkbox" checked={form.gerar_lancamento_caixa}
            onChange={(e) => setForm({ ...form, gerar_lancamento_caixa: e.target.checked })} />
          Gerar saída no caixa (DRE) — categoria "Acerto de Colaborador"
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm">Cancelar</button>
          <button onClick={submit} disabled={saving} data-testid="cc-acerto-confirm"
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-lg text-sm disabled:opacity-50">
            Confirmar Acerto
          </button>
        </div>
      </div>
    </ModalShell>
  );
}


// Bloco 9 Fev/2026 — Editar lançamento CC (propaga ao financeiro vinculado)
function EditLancamentoModal({ lanc, onClose, onSaved }) {
  // Horas em OS: pré-preenche a descrição completa (nº da O.S. + cálculo detalhado)
  const descricaoInicial = lanc.origem === 'horas_os'
    ? ((lanc.descricao || '').includes('#') ? lanc.descricao : descricaoHorasOs(lanc))
    : (lanc.descricao || '');
  const [form, setForm] = useState({
    data: lanc.data || todayISO(),
    valor: lanc.valor || '',
    descricao: descricaoInicial,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.valor || Number(form.valor) <= 0) { toast.error('Valor inválido'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/conta-corrente/lancamento/${lanc.id}`, {
        method: 'PATCH', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ ...form, valor: Number(form.valor) }),
      });
      if (r.ok) { toast.success('Lançamento atualizado'); onSaved(); }
      else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
    } finally { setSaving(false); }
  };
  return (
    <ModalShell title={`Editar lançamento — ${ORIGEM_LABELS[lanc.origem] || lanc.origem}`}
                onClose={onClose} testid="cc-edit-lanc-modal">
      <div className="space-y-3">
        {lanc.origem === 'horas_os' && lanc.os_numero && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded-lg p-2 text-[11px] flex items-start gap-2"
               data-testid="cc-edit-os-info">
            <Info size={12} className="text-yellow-300 shrink-0 mt-0.5" />
            <span>
              Crédito gerado pela <OsLink l={lanc} />
              {lanc.os_info?.maquina ? ` — ${lanc.os_info.maquina}` : ''}
              {lanc.os_info?.operation_type ? ` (${lanc.os_info.operation_type})` : ''}
              {lanc.os_info?.horas_atuais != null ? (
                <> — Horas atuais da O.S.: <strong data-testid="cc-edit-os-horas-atuais">{Number(lanc.os_info.horas_atuais).toFixed(2)}h</strong>
                  {Number(lanc.valor_hora_aplicado || 0) > 0 ? ` × R$ ${Number(lanc.valor_hora_aplicado).toFixed(2)}/h` : ''}</>
              ) : (lanc.horas ? ` — ${Number(lanc.horas).toFixed(2)}h × R$ ${Number(lanc.valor_hora_aplicado || 0).toFixed(2)}/h` : '')}
              {lanc.os_info?.horas_atuais != null && lanc.horas != null
                && Math.abs(Number(lanc.os_info.horas_atuais) - Number(lanc.horas)) > 0.005 && (
                <strong className="block mt-1 text-amber-300" data-testid="cc-edit-os-divergencia">
                  ⚠️ Crédito registrado com {Number(lanc.horas).toFixed(2)}h — divergente das horas atuais da O.S.
                  Use o painel "Revisão Horas O.S." para recalcular.
                </strong>
              )}
            </span>
          </div>
        )}
        {lanc.financeiro_lancamento_id && (
          <div className="bg-blue-500/10 border border-blue-500/30 text-blue-200 rounded-lg p-2 text-[11px] flex items-start gap-2">
            <Info size={12} className="text-blue-300 shrink-0 mt-0.5" />
            <span>As alterações de valor / data / descrição serão propagadas automaticamente para o lançamento financeiro vinculado.</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-gray-400">Data
            <input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
              data-testid="cc-edit-data"
              className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
          </label>
          <label className="text-xs text-gray-400">Valor (R$) *
            <input type="number" min="0" step="0.01" value={form.valor}
              onChange={(e) => setForm({ ...form, valor: e.target.value })}
              data-testid="cc-edit-valor"
              className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
          </label>
        </div>
        <label className="text-xs text-gray-400 block">Descrição
          <textarea rows={2} value={form.descricao}
            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            data-testid="cc-edit-descricao"
            className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm">Cancelar</button>
          <button onClick={submit} disabled={saving} data-testid="cc-edit-confirm"
            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg text-sm disabled:opacity-50">
            Salvar
          </button>
        </div>
      </div>
    </ModalShell>
  );
}


// Bloco 9 Fev/2026 — Conciliação retroativa de O.S. fechadas sem crédito CC
function ConciliacaoModal({ onClose, onApplied }) {
  const today = todayISO();
  const ago90 = (() => {
    const d = new Date(); d.setDate(d.getDate() - 90); return d.toISOString().slice(0, 10);
  })();
  const [di, setDi] = useState(ago90);
  const [df, setDf] = useState(today);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overrides, setOverrides] = useState({});  // wo_id -> valor_hora override

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/conta-corrente/conciliacao-os?data_inicio=${di}&data_fim=${df}`,
        { headers: authHeaders() });
      const d = await r.json();
      setItems(d.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const confirmar = async (it) => {
    const override = overrides[it.wo_id];
    if (it.valor_hora_pendente && (!override || Number(override) <= 0)) {
      toast.error('Informe o valor/hora para este operador antes de confirmar');
      return;
    }
    const body = override ? { valor_hora: Number(override) } : {};
    const r = await fetch(`${API_URL}/api/conta-corrente/conciliacao-os/${it.wo_id}/confirmar`, {
      method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    if (r.ok) {
      toast.success(`Crédito lançado para OS #${it.order_number || it.wo_id.slice(0, 6)}`);
      onApplied && onApplied();
      load();
    } else {
      const d = await r.json(); toast.error(d.detail || 'Erro ao confirmar');
    }
  };

  const ignorar = async (it) => {
    const motivo = window.prompt('Motivo (opcional):') || null;
    const r = await fetch(`${API_URL}/api/conta-corrente/conciliacao-os/${it.wo_id}/ignorar`, {
      method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ motivo }),
    });
    if (r.ok) { toast.success('OS ignorada'); load(); }
    else { toast.error('Erro ao ignorar'); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
           data-testid="cc-conciliacao-modal">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-[#1A1A1A] z-10">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <ClipboardCheck size={16} className="text-amber-400" />
            Conciliação retroativa de O.S.
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-lg p-3 text-xs flex items-start gap-2">
            <Info size={14} className="text-amber-300 shrink-0 mt-0.5" />
            <span>
              Lista as O.S. <strong>fechadas</strong> com horímetros preenchidos que ainda <strong>não geraram crédito</strong> na Conta Corrente do operador.
              Confirme uma a uma para criar o lançamento, ou marque como "ignorar" se o acerto já foi realizado fora do sistema.
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <label className="text-xs text-gray-400">De
              <input type="date" value={di} onChange={(e) => setDi(e.target.value)}
                data-testid="conciliacao-data-inicio"
                className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
            </label>
            <label className="text-xs text-gray-400">Até
              <input type="date" value={df} onChange={(e) => setDf(e.target.value)}
                data-testid="conciliacao-data-fim"
                className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
            </label>
            <button onClick={load} disabled={loading}
              data-testid="conciliacao-filter-btn"
              className="self-end px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-lg text-sm disabled:opacity-50">
              {loading ? 'Carregando...' : 'Filtrar'}
            </button>
          </div>

          {!loading && items.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm" data-testid="conciliacao-empty">
              <CheckCircle2 size={32} className="mx-auto mb-2 text-emerald-500" />
              Nenhuma O.S. pendente neste período.
            </div>
          )}

          {items.length > 0 && (
            <div className="overflow-x-auto border border-gray-800 rounded-lg">
              <table className="w-full text-xs" data-testid="conciliacao-tabela">
                <thead className="bg-[#0A0A0A] text-gray-400 uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">OS #</th>
                    <th className="text-left px-3 py-2">Data</th>
                    <th className="text-left px-3 py-2">Operador</th>
                    <th className="text-right px-3 py-2">Horas</th>
                    <th className="text-right px-3 py-2">R$/h</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-right px-3 py-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it.wo_id} className="border-t border-gray-800 hover:bg-gray-800/30">
                      <td className="px-3 py-2 text-gray-300 font-mono">#{it.order_number || it.wo_id.slice(0, 6)}</td>
                      <td className="px-3 py-2 text-gray-300">{it.date}</td>
                      <td className="px-3 py-2 text-gray-300">{it.operator_name}</td>
                      <td className="px-3 py-2 text-right text-gray-300 font-mono">{it.hours.toFixed(2)}h</td>
                      <td className="px-3 py-2 text-right">
                        {it.valor_hora_pendente ? (
                          <input type="number" min="0" step="0.01" placeholder="—"
                            value={overrides[it.wo_id] || ''}
                            onChange={(e) => setOverrides({ ...overrides, [it.wo_id]: e.target.value })}
                            data-testid={`conciliacao-vh-${it.wo_id}`}
                            className="w-20 bg-[#0A0A0A] border border-amber-500/50 rounded px-2 py-1 text-amber-200 text-xs text-right" />
                        ) : (
                          <span className="text-gray-300 font-mono">{formatBRL(it.valor_hora)}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {it.valor_hora_pendente
                          ? <span className="text-amber-400 italic">{overrides[it.wo_id]
                              ? formatBRL(Number(overrides[it.wo_id]) * it.hours)
                              : '—'}</span>
                          : <span className="text-emerald-400">{formatBRL(it.valor_calculado)}</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => confirmar(it)} data-testid={`conciliacao-confirm-${it.wo_id}`}
                            className="px-2 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded text-xs">
                            Confirmar
                          </button>
                          <button onClick={() => ignorar(it)} data-testid={`conciliacao-ignore-${it.wo_id}`}
                            className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-xs">
                            Ignorar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// Bloco 9 Parte 2 (Fev/2026) — Sync histórico de lançamentos do Financeiro com a CC
function SyncFinanceiroModal({ onClose, onApplied }) {
  const today = todayISO();
  const ago365 = (() => {
    const d = new Date(); d.setDate(d.getDate() - 365); return d.toISOString().slice(0, 10);
  })();
  const [di, setDi] = useState(ago365);
  const [df, setDf] = useState(today);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/conta-corrente/sync-financeiro?data_inicio=${di}&data_fim=${df}`,
        { headers: authHeaders() },
      );
      const d = await r.json();
      setItems(d.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const confirmar = async (it) => {
    const r = await fetch(
      `${API_URL}/api/conta-corrente/sync-financeiro/${it.lanc_id}/confirmar`,
      { method: 'POST', headers: authHeaders() },
    );
    if (r.ok) {
      toast.success(`Sincronizado: ${formatBRL(it.valor)} (${it.pessoa_nome})`);
      onApplied && onApplied();
      load();
    } else {
      const d = await r.json(); toast.error(d.detail || 'Erro');
    }
  };

  const ignorar = async (it) => {
    const motivo = window.prompt('Motivo (opcional):') || null;
    const r = await fetch(
      `${API_URL}/api/conta-corrente/sync-financeiro/${it.lanc_id}/ignorar`,
      { method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ motivo }) },
    );
    if (r.ok) { toast.success('Ignorado'); load(); }
    else { toast.error('Erro'); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-gray-800 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
           data-testid="cc-sync-fin-modal">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-[#1A1A1A] z-10">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Repeat size={16} className="text-blue-400" /> Sincronização histórica do Financeiro
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/30 text-blue-200 rounded-lg p-3 text-xs flex items-start gap-2">
            <Info size={14} className="text-blue-300 shrink-0 mt-0.5" />
            <span>
              Lança mentos do <strong>Financeiro</strong> com <strong>responsável vinculado</strong>
              {' '}a colaborador/operador que ainda não têm correspondência na Conta Corrente.
              Confirme um a um para sincronizar (<strong>Saídas → Retiradas</strong> /
              <strong> Entradas → Créditos</strong>) ou marque como ignorar caso já tenha sido tratado fora do sistema.
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <label className="text-xs text-gray-400">De
              <input type="date" value={di} onChange={(e) => setDi(e.target.value)}
                data-testid="sync-fin-data-inicio"
                className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
            </label>
            <label className="text-xs text-gray-400">Até
              <input type="date" value={df} onChange={(e) => setDf(e.target.value)}
                data-testid="sync-fin-data-fim"
                className="mt-1 w-full bg-[#0A0A0A] border border-gray-800 rounded px-3 py-2 text-white text-sm" />
            </label>
            <button onClick={load} disabled={loading}
              data-testid="sync-fin-filter-btn"
              className="self-end px-4 py-2 bg-blue-500 hover:bg-blue-400 text-black font-semibold rounded-lg text-sm disabled:opacity-50">
              {loading ? 'Carregando...' : 'Filtrar'}
            </button>
          </div>

          {!loading && items.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm" data-testid="sync-fin-empty">
              <CheckCircle2 size={32} className="mx-auto mb-2 text-emerald-500" />
              Nenhum lançamento pendente neste período.
            </div>
          )}

          {items.length > 0 && (
            <div className="overflow-x-auto border border-gray-800 rounded-lg">
              <table className="w-full text-xs" data-testid="sync-fin-tabela">
                <thead className="bg-[#0A0A0A] text-gray-400 uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Data</th>
                    <th className="text-left px-3 py-2">Categoria</th>
                    <th className="text-left px-3 py-2">Colaborador / Operador</th>
                    <th className="text-left px-3 py-2">Tipo CC</th>
                    <th className="text-right px-3 py-2">Valor</th>
                    <th className="text-right px-3 py-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it.lanc_id} className="border-t border-gray-800 hover:bg-gray-800/30">
                      <td className="px-3 py-2 text-gray-300">{it.data}</td>
                      <td className="px-3 py-2 text-gray-300" title={it.observacao}>{it.categoria_nome || '—'}</td>
                      <td className="px-3 py-2 text-gray-300">
                        {it.pessoa_nome}
                        <span className="text-[10px] text-gray-500 ml-1">({it.pessoa_tipo})</span>
                      </td>
                      <td className="px-3 py-2">
                        {it.sugestao_tipo_cc === 'credito'
                          ? <span className="text-emerald-400 text-[11px] font-semibold">Crédito (+)</span>
                          : <span className="text-rose-400 text-[11px] font-semibold">Retirada (−)</span>}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${
                        it.sugestao_tipo_cc === 'credito' ? 'text-emerald-400' : 'text-rose-400'
                      }`}>
                        {it.sugestao_tipo_cc === 'credito' ? '+' : '−'}{formatBRL(it.valor)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => confirmar(it)} data-testid={`sync-fin-confirm-${it.lanc_id}`}
                            className="px-2 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded text-xs">
                            Sincronizar
                          </button>
                          <button onClick={() => ignorar(it)} data-testid={`sync-fin-ignore-${it.lanc_id}`}
                            className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded text-xs">
                            Ignorar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ======================== Créditos Indevidos (Revisão + Estorno) ========================
// Correção Jul/2026 — Revisão de créditos de Horas em O.S. com cálculo inconsistente
// (valor lançado ≠ horas da O.S. × valor/hora atual do operador). O gestor decide:
// Recalcular (usa dados atuais) ou Manter (preserva o valor) — sempre com auditoria.
function RevisaoHorasOsModal({ onClose, onApplied }) {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const MOTIVO_LABELS = {
    divergencia: 'Divergência de cálculo',
    valor_hora_pendente: 'Valor/hora pendente',
    operador_sem_valor_hora: 'Operador sem valor/hora',
    os_sem_horimetro_final: 'O.S. sem horímetro final',
    os_nao_encontrada: 'O.S. não localizada',
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/conta-corrente/horas-os/revisao-inconsistentes`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setItens(d.itens || []);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const revisar = async (it, decisao) => {
    let motivo = null;
    if (decisao === 'recalcular') {
      const msg = `Recalcular o crédito da O.S. #${it.os_numero || '?'} de ${formatBRL(it.valor_atual)} para ${formatBRL(it.valor_esperado)} (${Number(it.horas_os || 0).toFixed(2)}h × R$ ${Number(it.valor_hora_operador || 0).toFixed(2)}/h)? A decisão fica registrada em auditoria.`;
      if (!window.confirm(msg)) return;
    } else {
      motivo = window.prompt(`Manter o valor atual de ${formatBRL(it.valor_atual)}? Informe o motivo (opcional) — a decisão fica registrada em auditoria:`);
      if (motivo === null) return; // cancelou
      motivo = motivo.trim() || null;
    }
    setProcessing(true);
    try {
      const r = await fetch(`${API_URL}/api/conta-corrente/horas-os/revisar/${it.lancamento_id}`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ decisao, motivo }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(decisao === 'recalcular'
          ? `Recalculado: ${formatBRL(d.valor_anterior)} → ${formatBRL(d.valor_novo)}`
          : 'Valor mantido — decisão registrada em auditoria');
        onApplied && onApplied();
        load();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Erro ao revisar lançamento');
      }
    } finally { setProcessing(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-gray-800 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
           data-testid="cc-revisao-horas-os-modal">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-[#1A1A1A] z-10">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <ClipboardCheck size={16} className="text-purple-400" />
            Revisão — Créditos de Horas em O.S.
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="cc-revisao-horas-os-close"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-purple-500/10 border border-purple-500/30 text-purple-200 rounded-lg p-3 text-xs flex items-start gap-2">
            <Info size={14} className="text-purple-300 shrink-0 mt-0.5" />
            <span>
              Lista créditos de <strong>Horas em O.S.</strong> cujo valor diverge de <strong>horas trabalhadas da O.S. × valor/hora atual do operador</strong>.
              Você pode <strong>Recalcular</strong> (usa os dados atuais) ou <strong>Manter</strong> o valor existente — ambas as decisões ficam registradas em auditoria (Regra de Ouro: nada é apagado).
            </span>
          </div>

          {loading ? (
            <p className="text-gray-400 text-sm text-center py-6">Carregando...</p>
          ) : itens.length === 0 ? (
            <p className="text-emerald-300 text-sm text-center py-6" data-testid="cc-revisao-horas-os-vazio">
              Nenhum crédito de Horas em O.S. inconsistente. ✅
            </p>
          ) : (
            <div className="overflow-x-auto border border-gray-800 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-[#0A0A0A] text-gray-400 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Pessoa</th>
                    <th className="px-3 py-2 text-left">O.S.</th>
                    <th className="px-3 py-2 text-left">Data</th>
                    <th className="px-3 py-2 text-left">Motivo</th>
                    <th className="px-3 py-2 text-right">Horas O.S.</th>
                    <th className="px-3 py-2 text-right">Valor/h atual</th>
                    <th className="px-3 py-2 text-right">Lançado</th>
                    <th className="px-3 py-2 text-right">Esperado</th>
                    <th className="px-3 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((it) => (
                    <tr key={it.lancamento_id} className="border-t border-gray-800" data-testid={`cc-revisao-row-${it.lancamento_id}`}>
                      <td className="px-3 py-2 text-white">{it.pessoa_nome || '—'}</td>
                      <td className="px-3 py-2">
                        {it.os_numero ? (
                          <a href={`/work-orders?search=${encodeURIComponent('#' + it.os_numero)}`}
                             className="text-yellow-400 hover:text-yellow-300 underline underline-offset-2 font-mono"
                             data-testid={`cc-revisao-os-link-${it.lancamento_id}`}>
                            #{it.os_numero}
                          </a>
                        ) : <span className="text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-300">{it.data}</td>
                      <td className="px-3 py-2 text-amber-300">{MOTIVO_LABELS[it.motivo] || it.motivo}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-300">{it.horas_os != null ? `${Number(it.horas_os).toFixed(2)}h` : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-300">{it.valor_hora_operador != null ? formatBRL(it.valor_hora_operador) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono text-rose-300">{formatBRL(it.valor_atual)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-300">{it.valor_esperado != null ? formatBRL(it.valor_esperado) : '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => revisar(it, 'recalcular')}
                            disabled={processing || !it.pode_recalcular}
                            title={it.pode_recalcular ? 'Recalcular com horas da O.S. × valor/hora atual' : 'Recálculo indisponível para este motivo'}
                            data-testid={`cc-revisao-recalcular-${it.lancamento_id}`}
                            className="px-2 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-30 text-emerald-300 border border-emerald-500/30 rounded text-xs">
                            Recalcular
                          </button>
                          <button onClick={() => revisar(it, 'manter')} disabled={processing}
                            title="Manter o valor existente (registra auditoria)"
                            data-testid={`cc-revisao-manter-${it.lancamento_id}`}
                            className="px-2 py-1 bg-gray-500/10 hover:bg-gray-500/20 disabled:opacity-30 text-gray-300 border border-gray-600 rounded text-xs">
                            Manter
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreditosIndevidosModal({ onClose, onApplied }) {
  const [itens, setItens] = useState([]);
  const [totalValor, setTotalValor] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});  // lancamento_id -> bool
  const [processing, setProcessing] = useState(false);

  const TIPO_LABEL = { 'Horista Puro': 'Horista Puro', 'CLT': 'CLT' };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/conta-corrente/salarios/revisao-indevidos`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setItens(d.itens || []);
        setTotalValor(d.total_valor || 0);
        setSelected({});
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const allSelected = itens.length > 0 && itens.every((i) => selected[i.lancamento_id]);
  const selectedIds = itens.filter((i) => selected[i.lancamento_id]).map((i) => i.lancamento_id);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(itens.map((i) => [i.lancamento_id, true])));
  };

  const estornar = async (ids) => {
    if (!ids || ids.length === 0) return;
    const isAll = ids.length === itens.length;
    const msg = isAll
      ? `Estornar TODOS os ${ids.length} crédito(s) indevido(s) (total ${formatBRL(totalValor)})? A ação é reversível (soft-delete) mas remove os créditos da Conta Corrente.`
      : `Estornar ${ids.length} crédito(s) selecionado(s)? A ação é reversível (soft-delete).`;
    if (!window.confirm(msg)) return;
    setProcessing(true);
    try {
      const r = await fetch(`${API_URL}/api/conta-corrente/salarios/estornar-indevidos`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(isAll ? {} : { lancamento_ids: ids }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`${d.estornados} crédito(s) estornado(s) — ${formatBRL(d.valor_total)} em ${d.pessoas_afetadas} pessoa(s)`);
        onApplied && onApplied();
        load();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Erro ao estornar');
      }
    } finally { setProcessing(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
           data-testid="cc-indevidos-modal">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-[#1A1A1A] z-10">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-400" />
            Créditos de Salário Indevidos
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="cc-indevidos-close"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-red-500/10 border border-red-500/30 text-red-200 rounded-lg p-3 text-xs flex items-start gap-2">
            <Info size={14} className="text-red-300 shrink-0 mt-0.5" />
            <span>
              Lista créditos de <strong>salário mensal automático</strong> gerados para colaboradores que <strong>não deveriam recebê-los</strong> (Horista Puro ou sem tipo de contrato definido).
              O estorno é <strong>reversível</strong> (soft-delete, nada é apagado fisicamente — Regra de Ouro) e recalcula o saldo da pessoa. Defina o Tipo de Contrato correto no cadastro antes, se necessário.
            </span>
          </div>

          {loading ? (
            <p className="text-gray-400 text-sm text-center py-6">Carregando...</p>
          ) : itens.length === 0 ? (
            <p className="text-emerald-300 text-sm text-center py-6" data-testid="cc-indevidos-vazio">
              Nenhum crédito indevido encontrado. ✅
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-gray-300">
                  <strong className="text-red-300">{itens.length}</strong> crédito(s) indevido(s) — total <strong className="text-red-300">{formatBRL(totalValor)}</strong>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => estornar(selectedIds)} disabled={processing || selectedIds.length === 0}
                    data-testid="cc-indevidos-estornar-selecionados"
                    className="px-3 py-2 text-xs bg-red-500/10 hover:bg-red-500/20 disabled:opacity-40 text-red-300 border border-red-500/30 rounded-lg">
                    Estornar selecionados ({selectedIds.length})
                  </button>
                  <button onClick={() => estornar(itens.map((i) => i.lancamento_id))} disabled={processing}
                    data-testid="cc-indevidos-estornar-todos"
                    className="px-3 py-2 text-xs bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white rounded-lg">
                    Estornar todos
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto border border-gray-800 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-[#0A0A0A] text-gray-400 text-xs">
                    <tr>
                      <th className="px-3 py-2 text-left">
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="cc-indevidos-select-all" />
                      </th>
                      <th className="px-3 py-2 text-left">Colaborador</th>
                      <th className="px-3 py-2 text-left">Tipo</th>
                      <th className="px-3 py-2 text-left">Competência</th>
                      <th className="px-3 py-2 text-right">Valor</th>
                      <th className="px-3 py-2 text-right">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((it) => (
                      <tr key={it.lancamento_id} className="border-t border-gray-800" data-testid={`cc-indevido-row-${it.lancamento_id}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={!!selected[it.lancamento_id]}
                            onChange={(e) => setSelected({ ...selected, [it.lancamento_id]: e.target.checked })}
                            data-testid={`cc-indevido-check-${it.lancamento_id}`} />
                        </td>
                        <td className="px-3 py-2 text-white">{it.pessoa_nome || '—'}</td>
                        <td className="px-3 py-2 text-gray-400">{it.tipo_contrato || 'Sem tipo'}</td>
                        <td className="px-3 py-2 text-gray-300">{it.competencia}</td>
                        <td className="px-3 py-2 text-right text-emerald-300">{formatBRL(it.valor)}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => estornar([it.lancamento_id])} disabled={processing}
                            data-testid={`cc-indevido-estornar-${it.lancamento_id}`}
                            className="px-2 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30 rounded text-xs">
                            Estornar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
